import type { Writable } from 'type-fest'
import type { DropdownChoice, DropdownChoiceId } from '@companion-app/shared/Model/Common.js'
import { useComputed } from '~/Resources/util'

export interface GroupBase<Option> {
	readonly options: readonly Option[]
	readonly label?: string
}

export interface DropdownChoiceGroup {
	label: string
	options: DropdownChoice[]
}

export type DropdownChoicesOrGroups =
	| DropdownChoice[]
	| DropdownChoiceGroup[]
	| Array<DropdownChoice | DropdownChoiceGroup>
	| Record<string, DropdownChoice>

export interface DropdownChoiceInt {
	value: DropdownChoiceId
	label: string
}

export type OptionsOrGroupsInt = readonly (DropdownChoiceInt | GroupBase<DropdownChoiceInt>)[]

export function useDropdownChoicesForSelect(choices: DropdownChoicesOrGroups): {
	options: OptionsOrGroupsInt
	flatOptions: DropdownChoiceInt[]
} {
	return useComputed(() => {
		if (Array.isArray(choices)) {
			const options: Writable<OptionsOrGroupsInt> = []
			const flatOptions: DropdownChoiceInt[] = []
			for (const item of choices) {
				if ('options' in item) {
					// Grouped choices
					const group: GroupBase<DropdownChoiceInt> = {
						label: String(item.label),
						options: item.options.map((choice): DropdownChoiceInt => ({
							value: choice.id,
							label: String(choice.label),
						})),
					}
					options.push(group)
					flatOptions.push(...group.options)
				} else {
					// Flat choice
					options.push({ value: item.id, label: String(item.label) })
					flatOptions.push({ value: item.id, label: String(item.label) })
				}
			}

			return {
				options,
				flatOptions,
			}
		} else if (typeof choices === 'object') {
			const options = Object.values(choices).map((choice): DropdownChoiceInt => ({
				value: choice.id,
				label: String(choice.label),
			}))
			return {
				options,
				flatOptions: options,
			}
		} else {
			return {
				options: [],
				flatOptions: [],
			}
		}
	}, [choices])
}
